import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Button,
  IconButton,
  TextField,
  Checkbox,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
  useTheme
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useSearchParams } from 'react-router-dom';
import UnitPageShell from '../components/shared/UnitPageShell';
import { parseSheetRowsToQuestions, QuizQuestion } from './units/quizPageFactory';
import {
  loadQuizOverrides,
  setOverrideQuestions,
  loadCustomQuizLinks,
  upsertCustomQuizLink,
  QuizEditorQuestion,
  QuizEditorLink
} from '../services/quizEditorStorage';

const QUIZ_PATH = '/assets/Quiz.xlsx';

const sanitizeQuestions = (questions: QuizEditorQuestion[]): QuizEditorQuestion[] =>
  questions.map((q) => {
    const options = q.options.slice();
    const inRange = q.correctIndexes.filter((idx) => idx >= 0 && idx < options.length);
    const correctIndexes = inRange.length > 0 ? inRange : options.length > 0 ? [0] : [];
    return { question: q.question, options, correctIndexes };
  });

// HOISTED QuestionEditor - außerhalb damit stabil bleibt
type QuestionEditorProps = {
  q: QuizEditorQuestion;
  qIndex: number;
  onUpdateQuestion: (index: number, value: string) => void;
  onUpdateOption: (qIndex: number, optIndex: number, value: string) => void;
  onToggleCorrect: (qIndex: number, optIndex: number) => void;
  onAddOption: (qIndex: number) => void;
  onRemoveOption: (qIndex: number, optIndex: number) => void;
};

const QuestionEditor: React.FC<QuestionEditorProps> = ({
  q,
  qIndex,
  onUpdateQuestion,
  onUpdateOption,
  onToggleCorrect,
  onAddOption,
  onRemoveOption
}) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%' }}>
    <TextField
      label="Frage"
      value={q.question}
      onChange={(e) => onUpdateQuestion(qIndex, e.target.value)}
      multiline
      minRows={1}
      maxRows={3}
      fullWidth
      size="small"
      autoComplete="off"
    />
    <Box sx={{ pl: { xs: 0, sm: 1 } }}>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
        Antworten (aktiviert = richtig):
      </Typography>
      <Stack spacing={0.5}>
        {q.options.map((opt, optIndex) => (
          <Box key={`opt-${qIndex}-${optIndex}`} sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <Checkbox
              checked={q.correctIndexes.includes(optIndex)}
              onChange={() => onToggleCorrect(qIndex, optIndex)}
              color="success"
              size="small"
              sx={{ p: 0.5, flexShrink: 0 }}
            />
            <TextField
              value={opt}
              onChange={(e) => onUpdateOption(qIndex, optIndex, e.target.value)}
              size="small"
              fullWidth
              placeholder={`Antwort ${optIndex + 1}`}
              autoComplete="off"
              sx={{ '& .MuiInputBase-input': { py: 0.75, fontSize: '0.875rem' } }}
            />
            <IconButton onClick={() => onRemoveOption(qIndex, optIndex)} size="small" color="error" sx={{ p: 0.5, flexShrink: 0 }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
      </Stack>
      <Button size="small" startIcon={<AddIcon />} onClick={() => onAddOption(qIndex)} sx={{ mt: 0.5, textTransform: 'none' }}>
        Antwort
      </Button>
    </Box>
  </Box>
);

const QuizEditorPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams] = useSearchParams();
  const [sheetOrder, setSheetOrder] = React.useState<string[]>([]);
  const [excelQuestions, setExcelQuestions] = React.useState<Record<string, QuizQuestion[]>>({});
  const [overrides, setOverrides] = React.useState<Record<string, QuizEditorQuestion[]>>(() => loadQuizOverrides());
  const [customLinks, setCustomLinks] = React.useState<QuizEditorLink[]>(() => loadCustomQuizLinks());
  const [selectedKey, setSelectedKey] = React.useState<string>('');
  const [editorQuestions, setEditorQuestions] = React.useState<QuizEditorQuestion[]>([]);
  const [dirty, setDirty] = React.useState(false);
  const [newDialogOpen, setNewDialogOpen] = React.useState(false);
  const [newQuizId, setNewQuizId] = React.useState('');
  const [newQuizTitle, setNewQuizTitle] = React.useState('');
  const [newQuizModule, setNewQuizModule] = React.useState('');
  const [expandedQuestion, setExpandedQuestion] = React.useState<number | false>(0);
  const initialKeySetRef = React.useRef(false);

  // Excel einmal laden
  React.useEffect(() => {
    const loadExcel = async () => {
      try {
        const resp = await fetch(QUIZ_PATH);
        if (!resp.ok) return;
        const buffer = await resp.arrayBuffer();
        const XLSX = await import('xlsx');
        const workbook = XLSX.read(buffer, { type: 'array' });
        const order = workbook.SheetNames;
        const map: Record<string, QuizQuestion[]> = {};
        order.forEach((sheetName) => {
          const sheet = workbook.Sheets[sheetName];
          const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 }) as any[][];
          map[sheetName] = parseSheetRowsToQuestions(rows);
        });
        setSheetOrder(order);
        setExcelQuestions(map);
      } catch {
        setSheetOrder([]);
        setExcelQuestions({});
      }
    };
    loadExcel();
  }, []);

  // Initialen Key setzen (nur einmal oder bei URL-Änderung)
  React.useEffect(() => {
    const queryQuiz = searchParams.get('quiz');
    if (queryQuiz) {
      setSelectedKey(queryQuiz.toUpperCase());
      initialKeySetRef.current = true;
      return;
    }
    // Nur Fallback setzen wenn noch nie gesetzt
    if (!initialKeySetRef.current && sheetOrder.length > 0) {
      const fallback = sheetOrder[0] || customLinks[0]?.id || Object.keys(overrides)[0] || '';
      if (fallback) {
        setSelectedKey(fallback);
        initialKeySetRef.current = true;
      }
    }
  }, [searchParams, sheetOrder, customLinks, overrides]);

  // Fragen laden wenn Key sich ändert
  React.useEffect(() => {
    if (!selectedKey) return;
    console.log('[QuizEditor] Loading questions für Key:', selectedKey);
    const override = overrides[selectedKey];
    const base = excelQuestions[selectedKey] || [];
    const fromCustom = customLinks.find((link) => link.id.toUpperCase() === selectedKey.toUpperCase());
    console.log('[QuizEditor] override:', override, 'base:', base, 'fromCustom:', fromCustom);
    const initial = override
      ? override
      : fromCustom
        ? []
        : base.map((q) => ({ question: q.question, options: q.options, correctIndexes: q.correctIndexes }));
    console.log('[QuizEditor] Loading', initial.length, 'Fragen');
    setEditorQuestions(initial.map((q) => ({
      question: q.question,
      options: q.options.slice(),
      correctIndexes: q.correctIndexes.slice()
    })));
    setDirty(false);
    setExpandedQuestion(0);
  }, [selectedKey, excelQuestions, overrides, customLinks]);

  const availableKeys = React.useMemo(() => {
    const keys: string[] = [];
    sheetOrder.forEach((key) => keys.push(key));
    customLinks.forEach((link) => { if (!keys.includes(link.id)) keys.push(link.id); });
    Object.keys(overrides).forEach((key) => { if (!keys.includes(key)) keys.push(key); });
    return keys;
  }, [sheetOrder, customLinks, overrides]);

  const labelForKey = (key: string) => {
    const custom = customLinks.find((link) => link.id.toUpperCase() === key.toUpperCase());
    return custom ? custom.title : key;
  };

  const sourceChip = () => {
    if (!selectedKey) return null;
    if (overrides[selectedKey]) return React.createElement(Chip, { label: 'Override', color: 'warning', size: 'small' });
    if (customLinks.some((link) => link.id.toUpperCase() === selectedKey.toUpperCase())) {
      return React.createElement(Chip, { label: 'Custom', color: 'info', size: 'small' });
    }
    return React.createElement(Chip, { label: 'Excel', color: 'success', size: 'small' });
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: string) => {
    if (newValue !== selectedKey) {
      setSelectedKey(newValue);
    }
  };

  const updateQuestionText = (index: number, value: string) => {
    setEditorQuestions((prev) => prev.map((q, idx) => idx === index ? { ...q, question: value } : q));
    setDirty(true);
  };

  const updateOptionText = (qIndex: number, optIndex: number, value: string) => {
    setEditorQuestions((prev) => prev.map((q, idx) => {
      if (idx !== qIndex) return q;
      const options = q.options.map((opt, oIdx) => (oIdx === optIndex ? value : opt));
      return { ...q, options };
    }));
    setDirty(true);
  };

  const toggleCorrect = (qIndex: number, optIndex: number) => {
    setEditorQuestions((prev) => prev.map((q, idx) => {
      if (idx !== qIndex) return q;
      const correctIndexes = q.correctIndexes.includes(optIndex)
        ? q.correctIndexes.filter((item) => item !== optIndex)
        : [...q.correctIndexes, optIndex];
      return { ...q, correctIndexes };
    }));
    setDirty(true);
  };

  const addOption = (qIndex: number) => {
    setEditorQuestions((prev) => prev.map((q, idx) => {
      if (idx !== qIndex) return q;
      return { ...q, options: [...q.options, ''] };
    }));
    setDirty(true);
  };

  const removeOption = (qIndex: number, optIndex: number) => {
    setEditorQuestions((prev) => prev.map((q, idx) => {
      if (idx !== qIndex) return q;
      const options = q.options.filter((_, index) => index !== optIndex);
      const correctIndexes = q.correctIndexes.filter((value) => value !== optIndex).map((value) => (value > optIndex ? value - 1 : value));
      return { ...q, options, correctIndexes };
    }));
    setDirty(true);
  };

  const addQuestion = () => {
    setEditorQuestions((prev) => [...prev, { question: '', options: ['', ''], correctIndexes: [0] }]);
    setExpandedQuestion(editorQuestions.length);
    setDirty(true);
  };

  const removeQuestion = (qIndex: number) => {
    setEditorQuestions((prev) => prev.filter((_, idx) => idx !== qIndex));
    setDirty(true);
  };

  const handleSave = () => {
    if (!selectedKey) return;
    const normalized = sanitizeQuestions(editorQuestions);
    console.log('[QuizEditor] handleSave: Speichere', selectedKey, 'mit', normalized.length, 'Fragen');
    console.log('[QuizEditor] Fragen-Daten:', normalized);
    setOverrideQuestions(selectedKey, normalized);
    const reloaded = loadQuizOverrides();
    console.log('[QuizEditor] Nach Speichern - reloaded overrides:', reloaded);
    console.log('[QuizEditor] Für Key', selectedKey, ':', reloaded[selectedKey]);
    setOverrides(reloaded);
    setDirty(false);
    console.log('[QuizEditor] handleSave fertig');
  };

  const handleCreateQuiz = () => {
    const id = newQuizId.trim().toUpperCase();
    if (!id) return;
    const title = newQuizTitle.trim() || id;
    const module = newQuizModule.trim() || (id.match(/^M\d+/i)?.[0]?.toUpperCase() ?? 'Custom');
    const slug = id.toLowerCase().endsWith('_quiz') ? id.toLowerCase() : `${id.toLowerCase()}_quiz`;
    const link: QuizEditorLink = { id, title, module, route: `/units/${slug}` };
    upsertCustomQuizLink(link);
    setCustomLinks(loadCustomQuizLinks());
    setOverrideQuestions(id, []);
    setOverrides(loadQuizOverrides());
    setSelectedKey(id);
    setNewDialogOpen(false);
    setNewQuizId('');
    setNewQuizTitle('');
    setNewQuizModule('');
  };

  return (
    <UnitPageShell title="Quiz-Editor" maxWidth={false}>
      <Box sx={{ px: { xs: 1, sm: 1.5, md: 2 }, py: { xs: 2, md: 3 }, width: '100%', boxSizing: 'border-box', minHeight: '100vh' }}>
        <Paper sx={{ p: { xs: 1.5, sm: 2, md: 2.5 }, maxWidth: '100%', boxSizing: 'border-box' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ fontWeight: 700 }}>Quiz-Editor</Typography>
            {sourceChip()}
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Button variant="outlined" size="small" startIcon={isMobile ? undefined : <AddIcon />} onClick={() => setNewDialogOpen(true)} sx={{ minWidth: 'auto', px: isMobile ? 1 : 2 }}>
              {isMobile ? '+' : 'Neues Quiz'}
            </Button>
            <Button variant="contained" size="small" startIcon={isMobile ? undefined : <SaveIcon />} onClick={handleSave} disabled={!dirty || !selectedKey} sx={{ minWidth: 'auto', px: isMobile ? 1.5 : 2 }}>
              {isMobile ? '💾' : 'Speichern'}
            </Button>
          </Box>
        </Box>

        {/* Tabs */}
        <Tabs
          value={availableKeys.includes(selectedKey) ? selectedKey : false}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 1.5, borderBottom: 1, borderColor: 'divider', '& .MuiTab-root': { minHeight: 36, minWidth: 'auto', py: 0.5, px: 1.5, textTransform: 'none', fontSize: { xs: '0.7rem', sm: '0.8rem' } } }}
        >
          {availableKeys.map((key) => (
            <Tab key={key} value={key} label={labelForKey(key)} />
          ))}
        </Tabs>

        {!selectedKey && <Typography variant="body2">Keine Quizdaten gefunden.</Typography>}

        {selectedKey && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                {editorQuestions.length} Fragen
              </Typography>
              <Button variant="outlined" size="small" startIcon={isMobile ? undefined : <AddIcon />} onClick={addQuestion} sx={{ minWidth: 'auto', px: isMobile ? 1 : 2 }}>
                {isMobile ? '+ Frage' : 'Frage'}
              </Button>
            </Box>

            {/* Mobile: Accordion-Ansicht */}
            {isMobile ? (
              <Box sx={{ mx: { xs: 0, sm: -0.5 } }}>
                {editorQuestions.map((q, qIndex) => (
                  <Accordion
                    key={`q-${selectedKey}-${qIndex}`}
                    expanded={expandedQuestion === qIndex}
                    onChange={(_, isExpanded) => setExpandedQuestion(isExpanded ? qIndex : false)}
                    sx={{ '&:before': { display: 'none' }, mb: 0.5, boxShadow: 'none', border: '1px solid', borderColor: 'divider' }}
                    disableGutters
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon fontSize="small" />}
                      sx={{
                        minHeight: 36,
                        px: 1,
                        alignItems: 'flex-start',
                        '& .MuiAccordionSummary-content': {
                          my: 0.5,
                          alignItems: 'flex-start',
                          gap: 0.5,
                          flexWrap: 'wrap',
                          minWidth: 0
                        },
                        '& .MuiAccordionSummary-expandIconWrapper': { mt: 0.25 }
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          maxWidth: '100%',
                          display: 'block',
                          whiteSpace: 'normal',
                          overflowWrap: 'anywhere',
                          wordBreak: 'break-word',
                          fontSize: '0.75rem',
                          lineHeight: 1.2
                        }}
                      >
                        {qIndex + 1}. {q.question || '(Neue Frage)'}
                      </Typography>
                      {q.correctIndexes.length > 0 && <CheckCircleIcon color="success" sx={{ fontSize: 14 }} />}
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0, px: 1, pb: 1 }}>
                      <QuestionEditor
                        q={q}
                        qIndex={qIndex}
                        onUpdateQuestion={updateQuestionText}
                        onUpdateOption={updateOptionText}
                        onToggleCorrect={toggleCorrect}
                        onAddOption={addOption}
                        onRemoveOption={removeOption}
                      />
                      <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button size="small" color="error" onClick={() => removeQuestion(qIndex)} sx={{ minWidth: 'auto', fontSize: '0.7rem' }}>
                          Löschen
                        </Button>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            ) : (
              /* Desktop: Kompakte Liste */
              <Stack spacing={1.5}>
                {editorQuestions.map((q, qIndex) => (
                  <Paper key={`q-desk-${selectedKey}-${qIndex}`} variant="outlined" sx={{ p: 1.5 }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 24 }}>{qIndex + 1}.</Typography>
                      <Box sx={{ flex: 1 }}>
                        <QuestionEditor
                          q={q}
                          qIndex={qIndex}
                          onUpdateQuestion={updateQuestionText}
                          onUpdateOption={updateOptionText}
                          onToggleCorrect={toggleCorrect}
                          onAddOption={addOption}
                          onRemoveOption={removeOption}
                        />
                      </Box>
                      <IconButton onClick={() => removeQuestion(qIndex)} color="error" size="small">
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Paper>
                ))}
              </Stack>
            )}

            {editorQuestions.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                Noch keine Fragen. Klicke auf "Frage" um zu beginnen.
              </Typography>
            )}
          </Box>
        )}
      </Paper>

      {/* Neues Quiz Dialog */}
      <Dialog open={newDialogOpen} onClose={() => setNewDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Neues Quiz</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField label="Quiz-ID (z.B. M1_U1_X99)" value={newQuizId} onChange={(e) => setNewQuizId(e.target.value)} size="small" fullWidth />
          <TextField label="Titel" value={newQuizTitle} onChange={(e) => setNewQuizTitle(e.target.value)} size="small" fullWidth />
          <TextField label="Modul (z.B. M1)" value={newQuizModule} onChange={(e) => setNewQuizModule(e.target.value)} size="small" fullWidth />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewDialogOpen(false)}>Abbrechen</Button>
          <Button variant="contained" onClick={handleCreateQuiz} disabled={!newQuizId.trim()}>Erstellen</Button>
        </DialogActions>
      </Dialog>
      </Box>
    </UnitPageShell>
  );
};

export default QuizEditorPage;
